import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { ROLE_LABEL } from "./UsersTab";

export const UserDialog = ({ user, onClose, onSaved }) => {
  const isNew = !user.id;
  const [f, setF] = useState({ name: user.name || "", email: user.email || "", mobile: user.mobile || "",
    role: user.role || "purchase_manager", active: user.active ?? true, password: "", pin: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (f.pin && !/^\d{4}$/.test(f.pin)) { setBusy(false); return toast.error("PIN must be exactly 4 digits"); }
      const body = { ...f, email: f.email || null, mobile: f.mobile || null, password: f.password || null, pin: f.pin || null };
      if (isNew) await api.post("/users", body);
      else await api.put(`/users/${user.id}`, body);
      toast.success(isNew ? "User added — share the mobile number and PIN with them" : "User updated");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-[95vw] overflow-y-auto sm:max-w-md" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isNew ? "Add user" : "Edit user"}</DialogTitle>
          <DialogDescription>They sign in with mobile number + 4-digit PIN (or email + password).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><label className="label">Name *</label><input className="field" required value={f.name} onChange={set("name")} data-testid="user-name-input" /></div>
          <div><label className="label">Email</label><input className="field" type="email" value={f.email} onChange={set("email")} data-testid="user-email-input" /></div>
          <div><label className="label">Mobile (10 digits)</label><input className="field mono" type="tel" inputMode="numeric" value={f.mobile} onChange={set("mobile")} data-testid="user-mobile-input" /></div>
          <div>
            <label className="label">Role</label>
            <select className="field" value={f.role} onChange={set("role")} data-testid="user-role-select">
              {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="label">{isNew || !user.has_pin ? "4-digit PIN (for mobile login)" : "Reset PIN (leave blank to keep)"}</label>
            <input className="field mono tracking-[0.5em]" inputMode="numeric" maxLength={4} value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, "") })} data-testid="user-pin-input" />
          </div>
          <div>
            <label className="label">{isNew ? "Temporary password (optional if PIN set)" : "Reset password (leave blank to keep)"}</label>
            <input className="field" minLength={6} value={f.password} onChange={set("password")} data-testid="user-password-input" />
          </div>
          {!isNew && (
            <label className="flex min-h-[48px] items-center gap-3 font-medium">
              <input type="checkbox" className="h-5 w-5" checked={f.active} onChange={set("active")} data-testid="user-active-checkbox" /> Active (can sign in)
            </label>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="btn btn-outline" onClick={onClose} data-testid="user-cancel-btn">Cancel</button>
            <button className="btn btn-primary" disabled={busy} data-testid="user-save-btn">{busy && <Loader2 size={18} className="animate-spin" />} Save</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
